import React, { FC, forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Box, FormHelperTextProps, Tooltip, ButtonBase } from '@mui/material';
import { WarningFilledIcon } from '@/src/components/icon/filled/WarningFilledIcon';
import { useElementScrollable } from '@/src/utils/useElementScrollable';
import { getFocusRingStyle } from '@/src/components/focusring';

/**
 * Custom extension of FormHelperText component to add interactive error icon
 */
export const FormHelperText: FC<FormHelperTextProps> = forwardRef(
  ({ children, sx, className, truncate, ...rest }, forwardedRef) => {
    const ref = useRef<HTMLParagraphElement>(null);
    useImperativeHandle(forwardedRef, () => ref.current);
    // changing width triggers re-render and updates max-width of tooltip that uses parent
    const [, setWidth] = useState(0);
    const { isScrollable, elementRef } = useElementScrollable('x', (el) => setWidth(el?.offsetWidth || 0));
    const tooltipActive =
      // contains error
      className?.includes('Mui-error') &&
      // we are truncating
      truncate !== false &&
      // HelperText or HelperTextContent scrollable
      (isScrollable || !!ref?.current?.querySelector('.Flow-scrollable'));

    return (
      <Tooltip
        title={children}
        placement="bottom-start"
        slotProps={{
          tooltip: {
            sx: {
              // match size of helper text
              maxWidth: ref?.current?.getBoundingClientRect()?.width
                ? `${ref.current.getBoundingClientRect().width - 24}px`
                : '398px',
              // hide right side of HelperTextContent
              '& .FlowHelperTextContent-textRight': {
                display: 'none',
              },
            },
          },
          arrow: {
            sx: {
              // remove default transform
              transform: 'none !important',
              // align with icon
              left: '17px !important',
            },
          },
        }}
        key={tooltipActive ? 'long-error-message' : 'disabled-tooltip'}
        {...(!tooltipActive && {
          open: false,
        })}
      >
        <Box
          component="p"
          className={className}
          sx={sx}
          {...(className?.includes('Mui-error') && {
            role: 'alert',
            sx: {
              '&&': {
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                ...(truncate !== false && {
                  // make sure interactive FocusRing from error is visible
                  overflow: 'visible',
                }),
              },
              ...sx,
            },
          })}
          ref={ref}
          {...rest}
        >
          {className?.includes('Mui-error') && (
            <ButtonBase
              sx={(theme) => ({
                cursor: 'default',
                borderRadius: '1px',
                ml: '2px',
                mr: '4px',
                ...getFocusRingStyle(theme, true, '0px'),
              })}
              aria-label="Error"
              className="FlowHelperText-error-button-active"
              {...(!tooltipActive && { tabIndex: -1, className: 'FlowHelperText-error-button' })}
            >
              <WarningFilledIcon sx={{ fontSize: '17px' }} />
            </ButtonBase>
          )}
          <Box
            sx={{
              display: 'block',
              overflow: truncate !== false ? 'hidden' : 'inherit',
              textOverflow: 'inherit',
              whiteSpace: 'inherit',
              flexGrow: '1',
            }}
            component="span"
            data-testid="FormHelperText-content"
            ref={elementRef}
          >
            {children}
          </Box>
        </Box>
      </Tooltip>
    );
  },
);
